import { useEffect, useState } from 'react';
import { CardWithTitle, DetailViewCard } from '../../Compounds/CardWithTitle';
import { Permission, TeamMember } from '../../../store/GeneralStore';
import { TextSpan } from '../../Atoms/Text';
import { fetchAssetTeam } from '../../../Api/Inventory/inventoryApi';
import {
  createProjectContact,
  deleteProjectContact,
  fetchProjectContacts,
  getProjectContactSubject,
  isProjectContact,
  ProjectContact,
  ProjectContactType
} from '../../../store/ProjectContact';
import { DeleteBtn } from '../../Compounds/DeleteBtn';
import { PermissibleRender } from '../../Compounds/PermissibleRender';
import { AddContactForm } from './AddContactForm';

export interface ProjectAssetContactsProps {
  assetId: string;
  projectId: string;
  accountId?: string;
  className?: string;
}

type ContactsBySection = {
  'On Site Contacts': ProjectContact[];
  'Supplier Contacts': ProjectContact[];
  'UPSTACK Contacts': TeamMember[];
};

const defaultContactsBySection: ContactsBySection = {
  'On Site Contacts': [],
  'Supplier Contacts': [],
  'UPSTACK Contacts': []
};

export function ProjectAssetContacts({ assetId, projectId, accountId, className }: ProjectAssetContactsProps) {
  const [contactsBySection, setContactsBySection] = useState<ContactsBySection>(defaultContactsBySection);

  const fetchUpstackContacts = async () => {
    const { data } = await fetchAssetTeam(assetId, accountId);

    if (data) {
      setContactsBySection((prev) => ({
        'On Site Contacts': prev['On Site Contacts'] || [],
        'Supplier Contacts': prev['Supplier Contacts'] || [],
        'UPSTACK Contacts': data
      }));
    }
  };

  const handleAddContact = async (contact: Omit<ProjectContact, 'id'>): Promise<ProjectContact | null> => {
    const result = await createProjectContact(contact);
    if (result) {
      fetchProjectContacts(projectId, assetId, true);
    }
    return result;
  };

  useEffect(() => {
    const projectContactSubject = getProjectContactSubject().subscribe((contacts) => {
      setContactsBySection((prev) => ({
        'On Site Contacts': contacts.filter((c) => c.type === ProjectContactType.ON_SITE) || [],
        'Supplier Contacts': contacts.filter((c) => c.type === ProjectContactType.SUPPLIER) || [],
        'UPSTACK Contacts': prev['UPSTACK Contacts'] || []
      }));
    });

    return () => {
      if (projectContactSubject) projectContactSubject.unsubscribe();
    };
  }, []);

  useEffect(() => {
    fetchUpstackContacts();
    if (projectId && assetId) fetchProjectContacts(projectId, assetId, true);
  }, [accountId, assetId, projectId]);

  const cardActions = () => {
    return (
      <PermissibleRender requiredPermissions={[Permission.CREATE_PROJECT_CONTACTS]}>
        <AddContactForm
          assetId={assetId}
          projectId={projectId}
          handleSave={handleAddContact}
        />
      </PermissibleRender>
    );
  };

  const renderContacts = () => {
    return Object.keys(contactsBySection).map((section) => (
      <CardWithTitle
        key={section}
        title={`${section} - ${contactsBySection[section as keyof ContactsBySection].length}`}
        titlePadding="p-1"
        className="relative border-b-2 border-grey-2 my-1"
        enableCollapse
        useDefaultCardStyles={false}
        collapsed={contactsBySection[section as keyof ContactsBySection].length === 0}>
        <div>
          {contactsBySection[section as keyof ContactsBySection].length > 0 ? (
            contactsBySection[section as keyof ContactsBySection].map((contact) => (
              <ContactRow
                key={contact.id}
                contact={contact}
                assetId={assetId}
                projectId={projectId}
              />
            ))
          ) : (
            <p className="text-sm text-grey11">No contacts available for this section.</p>
          )}
        </div>
      </CardWithTitle>
    ));
  };

  return (
    <DetailViewCard
      collapsed={true}
      title="Contacts"
      className={className}
      cardActions={cardActions()}
      enableCollapse>
      {renderContacts()}
    </DetailViewCard>
  );
}

const ContactRow = ({
  contact,
  projectId,
  assetId
}: {
  contact: TeamMember | ProjectContact;
  projectId: string;
  assetId: string;
}) => {
  const [showMore, setShowMore] = useState(false);
  const handleShowMore = () => {
    setShowMore((prev) => !prev);
  };

  const handleDeleteContact = async (contactId: string): Promise<boolean | undefined> => {
    const result = await deleteProjectContact(+contactId);

    if (result) {
      fetchProjectContacts(projectId, assetId, true);
    }

    return result;
  };

  const getContactName = () => {
    return isProjectContact(contact) ? contact.name : `${contact.firstName} ${contact.lastName}`;
  };

  return (
    <div className="pb-2 border-b-2 my-2">
      <div className="grid grid-cols-4 gap-2 place-items-start w-full">
        <TextSpan size="sm14">{getContactName()}</TextSpan>
        <TextSpan
          size="sm"
          className="col-span-2">
          {contact.email}
        </TextSpan>
        <TextSpan
          size="sm"
          color="indigo">
          <button onClick={handleShowMore}>{showMore ? 'Hide' : 'Show'} Details</button>
        </TextSpan>
      </div>
      {showMore && (
        <div className="mt-1">
          {!isProjectContact(contact) && contact.title && (
            <div className="grid grid-cols-4 gap-1 place-items-start my-1">
              <TextSpan
                size="sm"
                color="grey6">
                Title:
              </TextSpan>
              <TextSpan
                size="sm"
                className="col-span-2">
                {contact.title}
              </TextSpan>
            </div>
          )}
          {isProjectContact(contact) && contact.company && (
            <div className="grid grid-cols-4 gap-1 place-items-start my-1">
              <TextSpan
                size="sm"
                color="grey6">
                Company:
              </TextSpan>
              <TextSpan
                size="sm"
                className="col-span-2">
                {contact.company}
              </TextSpan>
            </div>
          )}
          <div className="grid grid-cols-4 gap-1 place-items-start my-1">
            <TextSpan
              size="sm"
              color="grey6">
              Phone:
            </TextSpan>
            <TextSpan
              size="sm"
              className="col-span-2">
              {contact.phone}
            </TextSpan>
            {isProjectContact(contact) && (
              <div className="flex justify-end w-full pr-14">
                <DeleteBtn
                  copyMap={{
                    default: 'Delete Contact',
                    deleting: 'Deleting Contact...',
                    error: 'Error Deleting Contact'
                  }}
                  deleteTriggerDescription={`Delete ${getContactName()} Contact`}
                  deleteItemId={contact.id.toString()}
                  deleteItem={handleDeleteContact}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
